import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { Separator } from '@/shared/components/ui/separator';
import { Textarea } from '@/shared/components/ui/textarea';
import { Progress } from '@/shared/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Phone,
  Plus,
  Pencil,
  Trash2,
  Play,
  Settings,
  Bot,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  Save,
  RefreshCw,
  Activity,
  Brain,
  FileText,
} from 'lucide-react';

const defaultQuestions = [
  {
    id: '1',
    question: 'Can you confirm your full name and current location?',
    category: 'general',
    order: 1,
    expectedKeywords: ['name', 'location', 'city'],
    isActive: true,
  },
  {
    id: '2',
    question: 'What is your current designation and company?',
    category: 'experience',
    order: 2,
    expectedKeywords: ['designation', 'company', 'role', 'position'],
    isActive: true,
  },
  {
    id: '3',
    question: 'How many years of total experience do you have?',
    category: 'experience',
    order: 3,
    expectedKeywords: ['years', 'experience', 'months'],
    isActive: true,
  },
  {
    id: '4',
    question: 'What is your current and expected salary?',
    category: 'salary',
    order: 4,
    expectedKeywords: ['salary', 'LPA', 'CTC', 'expected', 'current'],
    isActive: true,
  },
  {
    id: '5',
    question: 'What is your notice period?',
    category: 'availability',
    order: 5,
    expectedKeywords: ['notice', 'days', 'immediate', 'month'],
    isActive: true,
  },
  {
    id: '6',
    question: 'Why are you looking for a change from your current role?',
    category: 'general',
    order: 6,
    expectedKeywords: ['growth', 'opportunity', 'career', 'learning'],
    isActive: true,
  },
  {
    id: '7',
    question: 'Do you have any experience in the real estate sector?',
    category: 'skills',
    order: 7,
    expectedKeywords: ['real estate', 'property', 'housing', 'land'],
    isActive: true,
  },
  {
    id: '8',
    question: 'What are your key skills relevant to this position?',
    category: 'skills',
    order: 8,
    expectedKeywords: ['skills', 'technologies', 'tools', 'expertise'],
    isActive: true,
  },
  {
    id: '9',
    question: 'Are you available for an in-person interview this week?',
    category: 'availability',
    order: 9,
    expectedKeywords: ['yes', 'no', 'time', 'date'],
    isActive: true,
  },
  {
    id: '10',
    question: 'Do you have any questions about the role or company?',
    category: 'general',
    order: 10,
    expectedKeywords: ['queries', 'doubts', 'question'],
    isActive: true,
  },
];

const voiceSettings = {
  provider: 'twilio',
  voiceModel: 'gpt-4-turbo',
  voiceType: 'female',
  language: 'en-IN',
  speakingRate: 1.0,
  maxCallDuration: 180,
  silenceTimeout: 5,
  maxRetries: 3,
  retryInterval: 24,
  callWindowStart: '10:00',
  callWindowEnd: '19:00',
  welcomeMessage:
    'Hello, this is an automated call from Dharohar Land Corp HR team. You recently applied for a position with us. This call will take approximately 3 minutes to verify some details. Is this a good time to talk?',
  goodbyeMessage:
    'Thank you for your time. Our HR team will review your application and get back to you shortly. Have a great day!',
};

const masterDbFields = [
  { field: 'candidate_name', questionId: '1', mapped: true },
  { field: 'location', questionId: '1', mapped: true },
  { field: 'current_company', questionId: '2', mapped: true },
  { field: 'current_designation', questionId: '2', mapped: true },
  { field: 'experience', questionId: '3', mapped: true },
  { field: 'current_salary', questionId: '4', mapped: true },
  { field: 'expected_salary', questionId: '4', mapped: true },
  { field: 'notice_period', questionId: '5', mapped: true },
  { field: 'real_estate_experience', questionId: '7', mapped: true },
];

export default function VoiceAgentPage() {
  const [questions, setQuestions] = useState(defaultQuestions);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<(typeof defaultQuestions)[0] | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);

  const categoryColors: Record<string, string> = {
    general: 'bg-blue-100 text-blue-700',
    experience: 'bg-green-100 text-green-700',
    salary: 'bg-yellow-100 text-yellow-700',
    availability: 'bg-purple-100 text-purple-700',
    skills: 'bg-orange-100 text-orange-700',
  };

  const handleEditQuestion = (question: (typeof defaultQuestions)[0]) => {
    setEditingQuestion(question);
    setShowQuestionDialog(true);
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setShowQuestionDialog(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            AI Voice Agent Configuration
          </h1>
          <p className="text-muted-foreground">Configure automated voice calls to candidates</p>
        </div>
        <div className="flex w-full flex-col gap-2 md:flex-row lg:w-auto">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setIsTestMode(!isTestMode)}
          >
            <Play className="mr-2 h-4 w-4" />
            Test Agent
          </Button>
          <Button className="flex-1 sm:flex-none">
            <Save className="mr-2 h-4 w-4" />
            Save Configuration
          </Button>
        </div>
      </div>

      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-5">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <Bot className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">AI Voice Agent Status</h3>
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <p className="text-sm text-green-700">Ready to make calls</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center lg:flex lg:items-center">
              <div className="px-4 text-center">
                <p className="text-2xl font-bold">82%</p>
                <p className="text-xs text-muted-foreground">Success Rate</p>
              </div>
              <div className="border-l px-4 text-center">
                <p className="text-2xl font-bold">3.2</p>
                <p className="text-xs text-muted-foreground">Avg Duration (min)</p>
              </div>
              <div className="flex items-center justify-center">
                <Switch defaultChecked />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="questions" className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1 lg:grid-cols-4">
          <TabsTrigger value="questions" className="gap-2 py-3">
            <MessageSquare className="h-4 w-4" />
            Call Questions
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2 py-3">
            <Settings className="h-4 w-4" />
            Voice Settings
          </TabsTrigger>
          <TabsTrigger value="mapping" className="gap-2 py-3">
            <FileText className="h-4 w-4" />
            Master DB Mapping
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2 py-3">
            <Activity className="h-4 w-4" />
            Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="questions">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Predefined Questions</CardTitle>
                  <CardDescription>
                    Configure questions that the AI voice agent will ask candidates
                  </CardDescription>
                </div>
                <Button onClick={handleAddQuestion}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Question
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <Table className="min-w-[900px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Order</TableHead>
                      <TableHead className="min-w-[300px]">Question</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Expected Keywords</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {questions
                      .sort((a, b) => a.order - b.order)
                      .map((q) => (
                        <TableRow key={q.id}>
                          <TableCell>
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-600">
                              {q.order}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{q.question}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={categoryColors[q.category]}>
                              {q.category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {q.expectedKeywords.slice(0, 3).map((kw) => (
                                <Badge key={kw} variant="outline" className="text-xs">
                                  {kw}
                                </Badge>
                              ))}
                              {q.expectedKeywords.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{q.expectedKeywords.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {q.isActive ? (
                              <Badge className="bg-green-500">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditQuestion(q)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Provider</Label>
                    <Select defaultValue={voiceSettings.provider}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="twilio">Twilio</SelectItem>
                        <SelectItem value="vonage">Vonage</SelectItem>
                        <SelectItem value="plivo">Plivo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Voice Model</Label>
                    <Select defaultValue={voiceSettings.voiceModel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                        <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Voice Type</Label>
                    <Select defaultValue={voiceSettings.voiceType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Female - Professional</SelectItem>
                        <SelectItem value="male">Male - Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select defaultValue={voiceSettings.language}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en-IN">English (India)</SelectItem>
                        <SelectItem value="hi-IN">Hindi</SelectItem>
                        <SelectItem value="en-US">English (US)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Separator />
                <h4 className="font-semibold">Call Timing</h4>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Max Duration (seconds)</Label>
                    <Input type="number" defaultValue={voiceSettings.maxCallDuration} />
                  </div>
                  <div className="space-y-2">
                    <Label>Silence Timeout (seconds)</Label>
                    <Input type="number" defaultValue={voiceSettings.silenceTimeout} />
                  </div>
                  <div className="space-y-2">
                    <Label>Speaking Rate</Label>
                    <Input type="number" step="0.1" defaultValue={voiceSettings.speakingRate} />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Call Window Start</Label>
                    <Input type="time" defaultValue={voiceSettings.callWindowStart} />
                  </div>
                  <div className="space-y-2">
                    <Label>Call Window End</Label>
                    <Input type="time" defaultValue={voiceSettings.callWindowEnd} />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Retry Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Max Retry Attempts</Label>
                    <Input type="number" defaultValue={voiceSettings.maxRetries} />
                  </div>
                  <div className="space-y-2">
                    <Label>Retry Interval (hours)</Label>
                    <Input type="number" defaultValue={voiceSettings.retryInterval} />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Welcome Message</Label>
                  <Textarea className="min-h-[80px]" defaultValue={voiceSettings.welcomeMessage} />
                </div>
                <div className="space-y-2">
                  <Label>Goodbye Message</Label>
                  <Textarea className="min-h-[60px]" defaultValue={voiceSettings.goodbyeMessage} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="mapping">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Master Database Field Mapping
              </CardTitle>
              <CardDescription>Map voice agent responses to master database fields</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <Table className="min-w-[700px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Master DB Field</TableHead>
                      <TableHead>Mapped Question</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {masterDbFields.map((field) => {
                      const question = questions.find((q) => q.id === field.questionId);
                      return (
                        <TableRow key={field.field}>
                          <TableCell className="font-medium">{field.field}</TableCell>
                          <TableCell>
                            {question ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">Q{question.order}</Badge>
                                <span className="text-sm">{question.question.slice(0, 50)}...</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Not mapped</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {field.mapped ? (
                              <Badge className="bg-green-500">Mapped</Badge>
                            ) : (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Call Statistics</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex justify-between">
                      <span className="text-sm text-muted-foreground">Success Rate</span>
                      <span className="font-semibold">82%</span>
                    </div>
                    <Progress value={82} className="h-2" />
                  </div>
                  <div>
                    <div className="mb-2 flex justify-between">
                      <span className="text-sm text-muted-foreground">Completion Rate</span>
                      <span className="font-semibold">88%</span>
                    </div>
                    <Progress value={88} className="h-2" />
                  </div>
                  <div>
                    <div className="mb-2 flex justify-between">
                      <span className="text-sm text-muted-foreground">Data Accuracy</span>
                      <span className="font-semibold">94%</span>
                    </div>
                    <Progress value={94} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Call Outcomes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Completed Successfully</span>
                    </div>
                    <span className="font-semibold">128</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm">No Answer / Voicemail</span>
                    </div>
                    <span className="font-semibold">18</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm">Call Failed / Busy</span>
                    </div>
                    <span className="font-semibold">10</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</DialogTitle>
            <DialogDescription>
              Configure the question details and expected keywords
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="question">Question Text</Label>
              <Textarea
                className="min-h-[90px] resize-none"
                id="question"
                placeholder="Enter the question..."
                // className="min-h-[80px]"
                defaultValue={editingQuestion?.question}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select defaultValue={editingQuestion?.category || 'general'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="experience">Experience</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                    <SelectItem value="availability">Availability</SelectItem>
                    <SelectItem value="skills">Skills</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  defaultValue={editingQuestion?.order || questions.length + 1}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywords">Expected Keywords (comma separated)</Label>
              <Input
                id="keywords"
                placeholder="e.g., experience, years, total"
                defaultValue={editingQuestion?.expectedKeywords.join(', ')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="active">Active</Label>
                <p className="text-xs text-muted-foreground">Include in voice calls</p>
              </div>
              <Switch id="active" defaultChecked={editingQuestion?.isActive ?? true} />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              className="flex-1 sm:flex-none"
              onClick={() => setShowQuestionDialog(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1 sm:flex-none">
              <Save className="mr-2 h-4 w-4" />
              Save Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
